import { Dayjs } from "dayjs";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRangePicker, DateRange } from "@mui/x-date-pickers-pro";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import "./DateRangeWisePicker.css";

interface DateRangeWisePickerProps {
  setStartDate?: Dispatch<SetStateAction<Dayjs | null>>;
  setEndDate?: Dispatch<SetStateAction<Dayjs | null>>;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  disabled?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

export default function DateRangeWisePicker({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  disabled,
  minDate,
  maxDate,
}: DateRangeWisePickerProps) {
  const [value, setValue] = useState<DateRange<Dayjs>>([
    startDate ?? null,
    endDate ?? null,
  ]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const latestValueRef = useRef<DateRange<Dayjs>>([
    startDate ?? null,
    endDate ?? null,
  ]);

  // Sync internal value with parent startDate and endDate after close
  useEffect(() => {
    if (!open) {
      // Only sync if parent state has changed (avoids overwrite after onChange)
      if (
        startDate?.valueOf() !== latestValueRef.current[0]?.valueOf() ||
        endDate?.valueOf() !== latestValueRef.current[1]?.valueOf()
      ) {
        setValue([startDate ?? null, endDate ?? null]);
        latestValueRef.current = [startDate ?? null, endDate ?? null];
      }
    }
  }, [startDate, endDate, open]);

  // Handle picker close to update parent state
  const handleClose = () => {
    setOpen(false);

    if (latestValueRef.current[0] && latestValueRef.current[1]) {
      if (setStartDate) {
        setStartDate(latestValueRef.current[0]);
      }
      if (setEndDate) {
        setEndDate(latestValueRef.current[1]);
      }
      // Update latestValueRef to reflect committed values
      latestValueRef.current = [
        latestValueRef.current[0],
        latestValueRef.current[1],
      ];
    }
  };

  // Handle date changes
  const handleChange = (newValue: DateRange<Dayjs>) => {
    if (!disabled) {
      setValue(newValue);
      latestValueRef.current = newValue;
    }
  };

  // Used just to focus and trigger the picker manually
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const formatValue = (range: DateRange<Dayjs>) => {
    const [start, end] = range;
    if (!start && !end) return "";
    if (start && !end) return `${start.format("DD/MM/YY")} - `;
    if (!start && end) return ` - ${end.format("DD/MM/YY")}`;
    return `${start ? start.format("DD/MM/YY") : ""} - ${
      end ? end.format("DD/MM/YY") : ""
    }`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Visible single field input */}
      <TextField
        inputRef={inputRef}
        size="small"
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            // Focus hidden input to open picker
            setTimeout(() => hiddenInputRef.current?.focus(), 0);
          }
        }}
        value={formatValue(value)}
        placeholder="DD/MM/YY - DD/MM/YY"
        fullWidth
        inputProps={{ readOnly: true }}
        disabled={disabled}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: "13px",
            fontWeight: 500,
            letterSpacing: 0,
          },
          "& .MuiInputBase-input::placeholder": {
            fontSize: "11.5px",
            fontWeight: 500,
            letterSpacing: 0,
          },
        }}
      />

      {/* Hidden DateRangePicker with real inputs (visually hidden) */}
      <Box
        sx={{
          position: "absolute",
          visibility: "hidden",
          height: 0,
          overflow: "hidden",
        }}
      >
        <DateRangePicker
          value={value}
          minDate={minDate}
          maxDate={maxDate}
          onChange={handleChange}
          onClose={handleClose}
          open={open}
          renderInput={(startProps, endProps) => (
            <>
              <input ref={hiddenInputRef} {...startProps.inputProps} />
              <input {...endProps.inputProps} />
            </>
          )}
        />
      </Box>
    </LocalizationProvider>
  );
}
